// Analytics data: one request per location per game, through the same routes (and the same local
// dev test-data wrappers) as each vertical's own pages. A location's day is fetched once and every
// subject in it (the stand and its sections, the gates and each gate, ...) is read from that.
import moment from 'moment';
import { getConcessionAPI } from '../Concession/dummyLiveData';
import { getRetailAPI } from '../Retail/dummyLiveData';
import { getGatesAPI } from '../Gates/dummyLiveData';
import { getFacilitiesAPI } from '../Facilities/dummyLiveData';
import { fetchGatesDay } from '../Gates/gatesApi';
import { fetchFacilitiesDay } from '../Facilities/facilitiesApi';
import { processOccupancy, occupancyPoints } from '../Retail/overview/overviewMetrics';
import { toVenueTime } from '../Concession/live/liveMetrics';
import { timestampMinute, countSeries, queueSeries, combinedCountSeries, intervalFlow } from '../Gates/gatesMetrics';

// The schedule route, through the wrapper for the location's vertical
export const scheduleAPI = (vertical, location) => {
  if (vertical === 'retail') return getRetailAPI(location);
  if (vertical === 'concession') return getConcessionAPI(location);
  if (vertical === 'gates') return getGatesAPI(location);
  return getFacilitiesAPI(location);
};

const call = (api, route, body, authCtx) => new Promise((resolve, reject) => {
  api(route, body, (data, error) => (error ? reject(error) : resolve(data)), authCtx.token);
});

// Each reading averaged across the store's queue (or transaction) sensors, like the retail Overview
const averageAcross = (rows, field) => (rows || []).map((entry) => {
  const raw = entry[field];
  const values = raw && typeof raw === 'object'
    ? Object.values(raw).filter((v) => typeof v === 'number')
    : [typeof raw === 'number' ? raw : 0];
  const avg = values.length ? values.reduce((s, v) => s + v, 0) / values.length : 0;
  return { timestamp: entry.timestamp, value: avg };
});

const fetchRetailDay = async (location, authCtx, day) => {
  const api = getRetailAPI(location);
  const base = { timezone: location.timeZone, venue: authCtx.venue, locationId: location.id, measurement: location.measurement, date: day };
  const tasks = {};
  if (location.maxOccupancy > 0) {
    tasks.occupancy = call(api, 'get-retail-location-daily-data-v2', { ...base, zones: location.zones }, authCtx)
      .then((data) => processOccupancy(data, location, day));
  }
  if ((location.doorCounterSensorIds || []).length > 0) {
    tasks.traffic = call(api, 'get-location-daily-door-count-v2', {
      ...base,
      excludedSensorIds: [...(location.doorCounterSensorSalesIds || []), ...(location.doorCounterSensorStairsIds || [])],
      doorMeasurement: location.doorMeasurement,
    }, authCtx);
  }
  if ((location.queueTimeSensorIds || []).length > 0) {
    tasks.queue = call(api, 'get-retail-location-daily-queue-time', {
      ...base, queueMeasurement: location.queueMeasurement, queueTimeSensorIds: location.queueTimeSensorIds,
    }, authCtx).then((data) => averageAcross(data, 'queueTime'));
  }
  if (location.transactionMeasurement) {
    tasks.transaction = call(api, 'get-retail-location-daily-transaction-time', {
      ...base, transactionMeasurement: location.transactionMeasurement,
    }, authCtx).then((data) => averageAcross(data, 'transactionTime'));
  }
  // One failing measurement (e.g. no queue data that day) doesn't hide the others; a sign-in error does
  const keys = Object.keys(tasks);
  const settled = await Promise.allSettled(keys.map((k) => tasks[k]));
  const out = {};
  settled.forEach((r, i) => {
    if (r.status === 'fulfilled') out[keys[i]] = r.value;
    else if (r.reason?.status === 401) throw r.reason;
  });
  return out;
};

// { sectionId: [points] }, from the array of one-section objects the route returns
const fetchConcessionDay = async (location, authCtx, day) => {
  const data = await call(getConcessionAPI(location), 'get-concession-location-daily-data-v2', {
    sections: (location.sections || []).map((s) => s.sectionId),
    venue: authCtx.venue,
    locationId: location.id,
    measurement: location.measurement || '',
    timezone: location.timeZone,
    date: day,
  }, authCtx);
  const bySection = {};
  (Array.isArray(data) ? data : [data]).forEach((obj) => {
    Object.entries(obj || {}).forEach(([id, points]) => { bySection[String(id)] = points || []; });
  });
  return { sections: bySection };
};

// One location's raw day: retail { occupancy, traffic, queue, transaction }, concession { sections },
// gates { gates }, facilities { restrooms }
export const fetchLocationDay = async (vertical, location, authCtx, date) => {
  const day = moment(date).format('YYYY-MM-DD');
  if (vertical === 'retail') return fetchRetailDay(location, authCtx, day);
  if (vertical === 'concession') return fetchConcessionDay(location, authCtx, day);
  if (vertical === 'gates') return { gates: await fetchGatesDay(location, authCtx, day) };
  return { restrooms: await fetchFacilitiesDay(location, authCtx, day) };
};

export const locationDayKey = (vertical, locationId, date) => `${vertical}-${locationId}-${moment(date).format('YYYY-MM-DD')}`;

// ---------------------------------------------------------------------------
// One subject's day as minute series: { queue, transaction, count, occupancy, entries, exits },
// each [{ m, v }] with m = minute of the game day (after midnight continues past 1440).
// Entries and exits are per minute; the rest are readings (when a minute has two, the later wins).
// ---------------------------------------------------------------------------

const byMinuteLatest = (rows, dateKey, timeOf, valueOf) => {
  const map = new Map();
  [...(rows || [])]
    .map((r) => ({ r, t: timeOf(r) }))
    .filter(({ t }) => typeof t === 'string')
    .sort((a, b) => (a.t < b.t ? -1 : a.t > b.t ? 1 : 0))
    .forEach(({ r, t }) => {
      const m = timestampMinute(t, dateKey);
      const v = valueOf(r);
      if (m != null && typeof v === 'number' && !Number.isNaN(v)) map.set(m, v);
    });
  return [...map.entries()].sort((a, b) => a[0] - b[0]).map(([m, v]) => ({ m, v }));
};

// Average of several series minute by minute (only the series with a reading that minute)
const averageSeries = (list) => {
  const map = new Map();
  list.forEach((series) => series.forEach(({ m, v }) => {
    const b = map.get(m) || { sum: 0, n: 0 };
    b.sum += v;
    b.n += 1;
    map.set(m, b);
  }));
  return [...map.entries()].sort((a, b) => a[0] - b[0]).map(([m, b]) => ({ m, v: b.sum / b.n }));
};

// Cumulative counts -> people per minute
const flowSeries = (countsList) => {
  const flow = intervalFlow(combinedCountSeries(countsList), 1);
  return {
    entries: flow.map((b) => ({ m: b.start, v: b.in })),
    exits: flow.map((b) => ({ m: b.start, v: b.out })),
  };
};

const concessionSeries = (subject, raw, dateKey) => {
  const tz = subject.location.timeZone;
  const ids = subject.unitId != null ? [subject.unitId] : Object.keys(raw.sections || {});
  const perSection = ids.map((id) => (raw.sections?.[id] || []));
  const read = (field) => averageSeries(perSection.map((points) => byMinuteLatest(
    points, dateKey, (p) => toVenueTime(p.time, tz), (p) => Number(p[field]),
  )));
  if (subject.location.sensorDataType === 'count') return { count: read('peopleCount') };
  // Queue readings of 0 mean no reading (the backend's minimum is 1)
  const queue = averageSeries(perSection.map((points) => byMinuteLatest(
    points, dateKey, (p) => toVenueTime(p.time, tz), (p) => (Number(p.queueTime) > 0 ? Number(p.queueTime) : NaN),
  )));
  const transaction = averageSeries(perSection.map((points) => byMinuteLatest(
    points, dateKey, (p) => toVenueTime(p.time, tz), (p) => (Number(p.transactionTime) > 0 ? Number(p.transactionTime) : NaN),
  )));
  return { queue, transaction };
};

const retailSeries = (raw, dateKey) => {
  const out = {};
  if (raw.occupancy) out.occupancy = occupancyPoints(raw.occupancy.map((r) => r.total));
  if (raw.traffic) {
    // New entries between consecutive readings, in the order the route returns them, at each reading's
    // minute; drops (counter resets) don't count. Exactly how the retail Overview counts traffic, so the
    // numbers agree (the route can return several readings in one minute, so they're not merged first).
    const byMinute = new Map();
    for (let i = 1; i < raw.traffic.length; i += 1) {
      const m = timestampMinute(raw.traffic[i]?.timestamp, dateKey);
      const diff = (Number(raw.traffic[i]?.count) || 0) - (Number(raw.traffic[i - 1]?.count) || 0);
      if (m != null && diff > 0) byMinute.set(m, (byMinute.get(m) || 0) + diff);
    }
    out.entries = [...byMinute.entries()].sort((a, b) => a[0] - b[0]).map(([m, v]) => ({ m, v }));
  }
  if (raw.queue) out.queue = byMinuteLatest(raw.queue, dateKey, (r) => r.timestamp, (r) => (r.value > 0 ? r.value : NaN));
  if (raw.transaction) out.transaction = byMinuteLatest(raw.transaction, dateKey, (r) => r.timestamp, (r) => (r.value > 0 ? r.value : NaN));
  return out;
};

// Gates and restrooms share the same shape (rawData countIn/countOut, queueData)
const unitSeries = (units, dateKey) => {
  const flows = flowSeries(units.map((u) => countSeries(u, dateKey)));
  const queues = units.map((u) => queueSeries(u, dateKey).map((p) => ({ m: p.minute, v: p.queueTime })));
  const queue = averageSeries(queues);
  return { ...flows, ...(queue.length ? { queue } : {}) };
};

export const subjectSeries = (subject, raw, date) => {
  if (!raw) return null;
  const dateKey = moment(date).format('YYYY-MM-DD');
  if (subject.vertical === 'retail') return retailSeries(raw, dateKey);
  if (subject.vertical === 'concession') return concessionSeries(subject, raw, dateKey);
  if (subject.vertical === 'gates') {
    const gates = (raw.gates || []).filter((g) => subject.unitId == null || String(g.id) === subject.unitId);
    return unitSeries(gates, dateKey);
  }
  // Restroom entrances may come as "count" instead of "countIn" (the facilities pages read either)
  const restrooms = (raw.restrooms || [])
    .filter((r) => subject.unitId == null || String(r.restroomId) === subject.unitId)
    .map((r) => ({ ...r, rawData: (r.rawData || []).map((item) => ({ ...item, countIn: item.countIn ?? item.count })) }));
  return unitSeries(restrooms, dateKey);
};
